<template>
  <v-row>
    <v-col class="px-0">
      <v-card>
        <v-card-title>
          <v-row>
            <v-col cols="9" class="pb-0">
              <span class="pr-1">{{ title }}</span>

              <v-tooltip bottom max-width="300px">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <mf-icon class="help-icon pb-1" size="18">info</mf-icon>
                  </span>
                </template>
                <span>{{ productsTooltip }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <div v-if="raffle.rules.type === 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT'">
            <v-row>
              <v-col class="pt-0">
                <mf-info-card icon="info" :vHtml="acceleratorsProductsInfo" />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0">
                <toggle-button
                  v-model="lazyAccelerationOperation"
                  :disabled="isDisabled"
                  :options="accelerationOperationOptions"
                  :disabled-text="isConfirmPage ? '' : 'Não é possível alterar as regras de aceleração após ativação do sorteio.'"
                />
              </v-col>
            </v-row>
          </div>
          <v-row align="center">
            <v-col v-if="raffle.rules.type === 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT'" cols="4">
              <v-text-field
                v-model="lazyAccelerationValue"
                :disabled="isDisabled"
                class="text-field"
                :label="accelerationValueLabel"
                placeholder=""
                outlined
                :error-messages="errorAccelerationValueLabel"
                type="number"
              />
            </v-col>
            <v-spacer />
            <modal-graphql-search-content
              v-if="!isConfirmPage"
              button-activator
              button-text="Escolher produtos"
              :selected-items.sync="searchProducts"
              :is-segmentation="false"
              @confirm="insertProducts"
            />
            <div class="pr-3"></div>
            <mf-search v-model="search" style="max-width: 300px" hide-details placeholder="Busque um produto" @input="filterProducts" />
          </v-row>
          <h2 v-if="groupedProducts && groupedProducts.length > 0" class="mb-4">Produtos desagrupados</h2>
          <products-table class="mt-4" :products="ungroupedProducts" :disabled="isDisabled" :is-revision="isConfirmPage" @remove-item="removeItem" />

          <div v-if="groupedProducts && groupedProducts.length > 0" class="mt-8">
            <h2 class="mb-0">Grupos de produtos</h2>

            <div v-for="group in groupedProducts" :key="group._id">
              <hr class="my-4" />
              <div class="d-flex align-center justify-space-between">
                <h2>{{ group.name }}</h2>
                <mf-button label="Remover grupo" :disabled="group.disabled || isDisabled" text color="vermelho-d20" icon="delete" @click="removeItem(group)" />
              </div>
              <products-table
                class="mt-4"
                :products="group.products"
                :disabled="isDisabled"
                :is-revision="isConfirmPage"
                @remove-item="removeGroupItem($event, group)"
              />
            </div>
          </div>
          <p v-if="productsError" class="error--text error-text my-2 pa-2">É necessário adicionar ao menos um produto para ativar ou agendar um sorteio.</p>
          <div v-if="allProductsInactive" class="empty-products-error">É necessário conter ao menos um produto ativo</div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { localeString } from '@/config/helpers.js'
import findIndex from 'lodash/findIndex'
import flatten from 'lodash/flatten'
import cloneDeep from 'lodash/cloneDeep'
import { QUERY_VALIDATE_IMPORTED_PRODUCTS } from '@/modules/campanhas/gestao-ofertas-v2/graphql/index.js'
import ProductsTable from './ProductsTable.vue'
const eansValidation = (eans, product) => {
  if (product.products) return true
  return eans.length
}

export default {
  name: 'ParticipatingProducts',
  components: {
    ModalGraphqlSearchContent: () => import('@/components/ModalGraphqlSearchContent.vue'),
    ToggleButton: () => import('@/components/ToggleButton.vue'),
    ProductsTable,
  },
  provide: function () {
    return {
      objectSelected: this.formatImportedOffers,
      updateRequiredColumns: () => {},
    }
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
    productsError: Boolean,
    isConfirmPage: Boolean,
    eans: {
      type: Array,
      required: true,
    },
    statusCampaign: {
      type: String,
    },
    editable: {
      type: Boolean,
      required: true,
    },
    disable: {
      type: Boolean,
      required: false,
    },
    isDisabled: {
      type: Boolean,
    },
    accelerationValue: {
      type: Number,
    },
    accelerationOperation: {
      type: String,
    },
    accelerationValueError: {
      type: Boolean,
    },
  },
  data: () => ({
    dialog: false,
    loading: false,
    locale: localeString,
    expanded: [],
    search: '',
    searchProducts: [],
    openImportModal: false,
    importRequiredAnyOfFields: ['name', 'sku', 'ean'],
    importFields: [
      { text: 'SKU', value: 'sku' },
      { text: 'EAN', value: 'ean' },
      { text: 'Descrição', value: 'name' },
    ],
    productsInfo: {
      origin: 'search',
      original_eans: [],
    },
    allProductsInactive: false,
    wrongEAN: [],
    repeatedProducts: [],
    productsLocal: [],
    found: [],
    not_found: [],
    expandedRows: [],
    localAccelerationValue: null,
    localAccelerationOperation: 'ADD',
  }),
  computed: {
    ...mapState({
      raffle: (state) => state.sorteio.raffle,
      raffleStatus: (state) => state.sorteio.raffle.status,
      raffleStartDate: (state) => state.sorteio.raffle.start_date,
    }),
    ungroupedProducts() {
      if (!this.getProducts) return []
      return this.getProducts.filter((product) => !product?.isGroup)
    },
    groupedProducts() {
      if (!this.getProducts) return []
      return this.getProducts.filter((product) => product?.isGroup)
    },
    title() {
      if (this.raffle.rules.type === 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT') {
        return 'Produtos aceleradores'
      } else {
        return 'Produtos vigentes'
      }
    },
    productsTooltip() {
      if (this.raffle.rules.type === 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT') {
        return 'Produtos que irão conceder números da sorte extras.'
      } else {
        return 'Produtos que serão válidos para concorrer a um número da sorte.'
      }
    },
    acceleratorsProductsInfo() {
      return '<span style="color: rgb(0, 77, 153)">Cupons contendo os produtos aceleradores concederão números da sorte extras ao consumidor. Esta modalidade apenas <b>valida se o cupom possui ao menos um dos produtos selecionados</b> para conceder os números da sorte extras. Ou seja, caso o cupom tenha múltiplos produtos selecionados, sejam eles diferentes ou iguais, a <b>regra de números extras será aplicada apenas uma vez</b>.</span>'
    },
    // se houver busca na barra de pesquisa, retorna a lista filtrada, senao retorna todos os produtos
    getProducts() {
      return this.search !== '' ? this.productsLocal : this.products
    },
    listAllProducts() {
      return this.products.reduce((productList, product) => {
        if (product.products) {
          const formattedProducts = product.products.map((groupProduct) => ({ ...groupProduct, groupName: product.name }))
          productList.push(...formattedProducts)
        } else {
          productList.push(product)
        }

        return productList
      }, [])
    },
    lazyAccelerationValue: {
      get() {
        return this.localAccelerationValue
      },
      set(value) {
        const intValue = parseInt(value)
        this.localAccelerationValue = intValue
        this.$emit('update:acceleration-value', intValue)
      },
    },
    lazyAccelerationOperation: {
      get() {
        return this.localAccelerationOperation
      },
      set(value) {
        this.localAccelerationOperation = value
        this.$emit('update:acceleration-operation', value)
      },
    },
    accelerationOperationOptions() {
      return [
        {
          label: 'Adicionar números extras',
          value: 'ADD',
          tooltip:
            'O consumidor irá ganhar a quantidade informada abaixo de números da sorte extra caso leve um dos produtos participantes, independente da quantidade de produtos.',
        },
        {
          label: 'Multiplicar números',
          value: 'MULTIPLY',
          tooltip: 'O consumidor terá seus números da sorte do cupom multiplicados pelo valor informado abaixo, independente da quantidade de produtos',
        },
      ]
    },
    errorAccelerationValueLabel() {
      if (this.accelerationValueError) {
        return 'Valor para o acelerador deve ser fornecido.'
      }
      return ''
    },
    accelerationValueLabel() {
      if (this.localAccelerationOperation === 'ADD') {
        return 'Números da sorte a adicionar'
      } else if (this.localAccelerationOperation === 'MULTIPLY') {
        return 'Multiplicador de números da sorte'
      }
      return ''
    },
  },
  watch: {
    products() {
      const clonedProducts = cloneDeep(this.products)
      if (clonedProducts.length > 0) {
        this.eans.length = 0
        const eanArray = clonedProducts.reduce((accumulator, item) => {
          if (item.products) {
            item.products.forEach((product) => {
              const { sku, eans } = product
              accumulator.push({ sku, eans })
            })
          } else {
            const { sku, eans } = item
            accumulator.push({ sku, eans })
          }
          return accumulator
        }, [])
        this.eans.push(...eanArray)
      }
    },
    accelerationValue(value) {
      this.localAccelerationValue = value
    },
    accelerationOperation(value) {
      this.localAccelerationOperation = value
    },
  },
  created() {
    if (this.statusCampaign) {
      this.searchProducts = this.products
      this.insertProducts(this.products)
    }
  },
  mounted() {
    this.localAccelerationValue = this.accelerationValue
    if (this.accelerationOperation) {
      this.localAccelerationOperation = this.accelerationOperation
    } else {
      this.lazyAccelerationOperation = 'ADD'
    }
  },
  methods: {
    verifyEanQty(eans, ean) {
      if (eans.length === 1 && eans.includes(ean)) {
        return true
      }

      return false
    },
    verifyExpandIcon({ expanded, onExpand, record }) {
      if (record.product && record.product.eans.length === 1) return

      return expanded ? (
        <mf-button
          color="marca-1"
          type="icon"
          size="sm"
          icon="indeterminate_check_box"
          onClick={(e) => onExpand(record, e)}
          style="!record.products ? 'padding-left: 30px;' : ''"
        />
      ) : (
        <mf-button
          color="marca-1"
          type="icon"
          size="sm"
          icon="add_box"
          onClick={(e) => onExpand(record, e)}
          style="!record.products ? 'padding-left: 30px;' : ''"
        />
      )
    },
    toSet(arr) {
      return Object.keys(
        arr.reduce(function (seen, val) {
          seen[val] = true
          return seen
        }, {})
      )
    },
    filterProducts(searchKey) {
      let arrayOfMatchedObjects = this.products.filter((object) => {
        return JSON.stringify(object).toString().toLowerCase().includes(searchKey.toLowerCase())
      })
      this.productsLocal = searchKey !== '' ? arrayOfMatchedObjects : []
    },
    insertProducts(groupProducts) {
      this.loading = true
      let clonedProducts = []

      groupProducts.forEach((product, index) => {
        if (!product) return
        const productIndex = findIndex(this.products, (clonedProduct) => {
          return (clonedProduct?._id && clonedProduct?._id === product?._id) || (clonedProduct?.sku && clonedProduct?.sku === product?.sku)
        })

        if (productIndex >= 0) {
          let newProduct = {}
          if (product?.products) {
            const { products, imported, ...groupWithoutProducts } = this.products[productIndex]
            newProduct = Object.assign(this.formatProduct(product, index, imported), groupWithoutProducts)
          } else {
            const isImported = false
            newProduct = Object.assign(this.formatProduct(product, index, isImported), this.products[productIndex])
          }

          clonedProducts.push(newProduct)
        } else {
          const isImported = true
          clonedProducts.push(this.formatProduct(product, index, isImported))
        }
      })

      this.updateProductList(clonedProducts).then(() => {
        this.searchProducts = clonedProducts
        this.checkRepeatedProducts()
      })
    },

    formatProduct(product, index, isImported) {
      let newProduct = {
        isNew: true,
      }
      if (product?.products) {
        const formattedProducts = product.products.map((item) => {
          return {
            isGroup: true,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            active: typeof item.active === 'boolean' ? item.active : true,
            sku: item?.sku,
            ean: item.ean,
            _id: item._id,
            eans: item.eans.length === 1 ? [item.ean] : item.eans,
            name: item.name,
            origin: 'search',
            original_eans: [],
            product: item.product
              ? item.product
              : {
                  ean: item.ean,
                  eans: item.eans.length > 1 ? item.eans : [item.ean],
                },
            index,
          }
        })
        newProduct = Object.assign(
          {
            _id: product?._id,
            isGroup: true,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            name: product?.name,
            products: formattedProducts,
            index,
          },
          this.productsInfo
        )
      } else {
        newProduct = Object.assign(
          {
            _id: product?._id,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            active: typeof product?.active === 'boolean' ? product?.active : true,
            sku: product?.sku,
            ean: product?.ean,
            eans: product?.eans?.length === 1 ? [product?.ean] : product?.eans,
            name: product?.name,
            product: {
              ean: product?.ean,
              eans: product?.eans?.length > 1 ? product?.eans : [product?.ean],
            },
            index,
          },
          this.productsInfo
        )
      }
      return newProduct
    },
    isDisabledProduct(isNewProduct) {
      return this.raffleStatus !== 'DRAFT' && isNewProduct !== true
    },
    async formatImportedOffers({ objects, selectedColumns }) {
      let idField = null
      let ids = []
      if (selectedColumns.includes('sku')) {
        idField = 'sku'
        ids = objects.map(({ sku }) => sku)
      } else if (selectedColumns.includes('ean')) {
        idField = 'ean'
        ids = objects.map(({ ean }) => ean)
      } else {
        this.$alert({
          title: 'Erro na importação',
          message: 'O arquivo importado precisa conter SKU ou EAN',
        })
        return
      }
      ids = ids.filter((item) => item !== '')

      const payload = {}
      payload[`${idField}s`] = ids

      await this.$apollo
        .query({
          query: QUERY_VALIDATE_IMPORTED_PRODUCTS,
          variables: payload,
        })
        .then((response) => {
          this.found = response.data.products.products
          let allValues = []
          this.found.forEach((prod) => {
            allValues = [...allValues, ...flatten(prod.eans), prod.ean, prod?.sku]
          })
          this.not_found = ids.filter((item) => {
            return allValues.indexOf(item) === -1
          })
        })
      let confirmed = true
      if (this.not_found.length) {
        confirmed = await this.$confirm({
          message: `Os seguintes ${this.not_found.length} produto(s) não foram encontrados:\n ${this.not_found.join('\n')}
          \n Deseja continuar importação?`,
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) {
        return
      }

      const checkedProducts = this.found.reduce((acc, product) => {
        objects.forEach((object) => {
          if (product.ean === object[idField] || product?.sku === object[idField] || product.eans.indexOf(object[idField]) !== -1) {
            object[idField] = product[idField]
          }
        })
        return { ...acc, [product[idField]]: product }
      }, {})
      const productsToInsert = []
      const insertedIds = []

      objects.forEach((product, index) => {
        if (checkedProducts[product[idField]]) {
          productsToInsert.push({
            imported: true,
            active: true,
            price_1: product.price_1 ? parseFloat(product.price_1.replace(',', '.')) : 0,
            price_2: product.price_2 ? parseFloat(product.price_2.replace(',', '.')) : 0,
            additional_points_rate: product.additional_points_rate ? parseInt(product.additional_points_rate.replace(',', '.')) : 0,
            maximum_per_client: product.maximum_per_client || 0,
            minimum_per_client: product.minimum_per_client || 0,
            name: product.name || checkedProducts[product[idField]].name,
            sku: checkedProducts[product[idField]]?.sku,
            ean: checkedProducts[product[idField]].ean,
            eans: checkedProducts[product[idField]].eans.length === 1 ? [checkedProducts[product[idField]].ean] : checkedProducts[product[idField]].eans,
            original_eans: [],
            product: {
              ean: checkedProducts[product[idField]].ean,
              eans: checkedProducts[product[idField]].eans.length > 1 ? checkedProducts[product[idField]].eans : [checkedProducts[product[idField]].ean],
            },
          })
        }
        insertedIds.push(product[idField])
      })

      let clonedProducts = cloneDeep(this.products)
      const allProducts = clonedProducts.concat(productsToInsert).reduce((products, product) => {
        return products.some((p) => (p?.sku && p?.sku === product?.sku) || (p._id && p._id === product._id)) ? products : [...products, product]
      }, [])
      this.searchProducts = allProducts
      this.$emit('update:products', allProducts)
    },
    checkRepeatedProducts() {
      this.repeatedProducts = []
      let disableButtons = false
      const productsArray = this.listAllProducts

      productsArray.forEach((product, forEachIndex) => {
        const foundObject = productsArray.find((element, findIndex) => {
          if (forEachIndex !== findIndex && element?.sku === product?.sku && (element?.eans?.[0] === product?.eans?.[0] || element?.ean === product?.ean)) {
            return element
          }
        })

        if (foundObject && product?.sku && product?.eans[0]) {
          disableButtons = true
          this.repeatedProducts.push(foundObject)
        }
      })
      this.$emit('disable-button', disableButtons)
    },
    removeItem(product) {
      this.loading = true
      let clonedProducts = cloneDeep(this.products)
      let productIndex
      let searchProductIndex

      if (product.products) {
        productIndex = clonedProducts.findIndex((clonedProduct) => clonedProduct._id === product._id)
        searchProductIndex = this.searchProducts.findIndex((searchProduct) => searchProduct._id === product._id)
      } else {
        productIndex = clonedProducts.findIndex((clonedProduct) => clonedProduct?.sku === product?.sku)
        searchProductIndex = this.searchProducts.findIndex((searchProduct) => searchProduct?.sku === product?.sku)
      }

      clonedProducts.splice(productIndex, 1)
      this.searchProducts.splice(searchProductIndex, 1)
      this.updateProductList(clonedProducts).then(() => {
        this.checkRepeatedProducts()
        this.formatExpandedRows(productIndex)
      })
    },
    removeGroupItem(product, group) {
      this.loading = true
      const { _id } = group
      const groupIndex = this.products.findIndex((clonedGroup) => clonedGroup._id === _id)
      const groupSearchArrayIndex = this.searchProducts.findIndex((clonedGroup) => clonedGroup._id === _id)
      const { products: productsGroup, ...newGroup } = this.searchProducts[groupSearchArrayIndex]
      const productIndex = productsGroup.findIndex((productGroup) => productGroup?.sku === product?.sku)

      if (productIndex >= 0) {
        let clonedProducts = cloneDeep(this.products)
        productsGroup.splice(productIndex, 1)
        clonedProducts[groupIndex].products.splice(productIndex, 1)

        if (productsGroup.length > 0) {
          const updatedGroup = Object.assign(newGroup, { products: productsGroup })
          this.searchProducts[groupIndex] = updatedGroup
        } else {
          this.searchProducts.splice(groupIndex, 1)
          clonedProducts.splice(groupIndex, 1)
        }
        this.updateProductList(clonedProducts).then(() => {
          if (productsGroup.length === 0) this.formatExpandedRows(groupIndex)

          this.checkRepeatedProducts()
        })
      }
    },
    updateProductList(productsArray) {
      return new Promise((resolve) => {
        this.$emit('update:products', productsArray)
        this.loading = false
        resolve()
      })
    },
    checkValidations() {
      const emptyProducts = Boolean(this.products.length === 0)
      this.products.forEach((item, index) => {
        if (item.eans && item.eans.length === 0) this.wrongEAN[index] = true
        else this.wrongEAN = []
      })
      this.$v.$touch()
      if (this.$v.$error || emptyProducts) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return false
      }
      return true
    },
    getEansCount(item) {
      const {
        eans: eansItem,
        product: { eans: eansProduct },
      } = item
      let eans = []
      if (eansItem.hasOwnProperty('sku')) {
        eans.push(...eansItem.eans)
      } else {
        eans.push(...eansItem)
      }

      if (!eans || eans.length === 0) {
        return `Selecionar EAN`
      } else if (eans.length < eansProduct.length) {
        return `${eans.length}/${eansProduct.length}`
      } else if (eans.length >= eansProduct.length) {
        // If an EAN was deleted on the product but remains on the offer, offer's eans >= product's eans
        return 'Todos'
      }
    },
    addErrorClass(sku, ean, name) {
      const hasRepeatedProduct = this.repeatedProducts.findIndex((product) => {
        return product?.sku === sku || product?.ean === ean || product?.groupName === name
      })

      return hasRepeatedProduct >= 0
    },
    formatExpandedRows(productIndex) {
      const newExpandedRows = this.expandedRows.reduce((rowsArray, currentRow) => {
        if (this.expandedRows.length === 1 || currentRow === productIndex) return rowsArray
        else if (currentRow > productIndex) {
          rowsArray.push(currentRow - 1)
        } else {
          rowsArray.push(currentRow)
        }
        return rowsArray
      }, [])

      this.expandedRows = newExpandedRows
    },
  },
  validations() {
    return {
      products: {
        required,
        $each: {
          sku: requiredIf(function (group) {
            return !group.products
          }),
          name: { required, notOnlySpace },
          eans: {
            eansValidation,
          },
          products: {
            $each: {
              eans: {
                eansValidation,
              },
              name: { required, notOnlySpace },
            },
          },
        },
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.ant-table-expanded-row {
  position: relative;
}
.error--text {
  font-size: 12px;
}
.table-align {
  position: relative;
  left: -60px;
  top: 0;
  width: calc(100% + 80px);
}
.products-on-offer {
  td {
    min-height: 100px;
  }
  th {
    font-size: 14px;
    color: $cinza-d10 !important;
  }
  .repeated {
    background-color: #ffc4c4;
    &:hover {
      background-color: #ffc4c4 !important;
    }
  }
  .link {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 150%;
    letter-spacing: 0.01em;
    color: $marca-0-l40 !important;
    text-transform: none;
    text-decoration-line: none;
    &:hover {
      text-decoration-line: underline;
    }
  }
  .expandLabel {
    display: block;
    cursor: pointer;
  }
  .titulo_card {
    font-size: 24px;
    font-weight: bold;
    color: $cinza-d20;
  }

  .products-table {
    .theme--light.v-text-field--outlined.v-input--is-focused:not(.v-input--has-state):not(.v-input--is-disabled) > .v-input__control > .v-input__slot fieldset,
    .v-application .primary--text {
      border-color: $marca-1 !important;
      caret-color: $marca-1 !important;
      padding: 0 5px;
    }
    tr {
      &:hover {
        .v-text-field {
          .v-input__slot {
            background-color: $branco !important;
          }
        }
        .money-input {
          background-color: $branco !important;
        }
      }
      &.repeated {
        background-color: #ffc4c4;
        &:hover {
          background-color: #ffc4c4 !important;
        }
        .v-input__slot,
        .money-input {
          background-color: transparent !important;
          border: none !important;
        }
      }
    }
    .v-text-field--enclosed .v-input__append-inner {
      margin-top: 2px !important;
    }
    .v-text-field {
      input {
        line-height: unset !important;
      }
      .v-input__slot {
        padding-left: 5px !important;
        margin-bottom: 0;
        font-size: 0.875rem;
        min-height: 30px !important;
        box-shadow: none !important;
        border: solid 1px #d9d9d9 !important;
        background-color: #f3f3f3 !important;
      }
    }
    .v-text-field__details {
      display: none !important;
    }
    .v-text-field__prefix {
      color: $preto;
      padding-right: 3px;
    }
    input {
      padding: 0px;
    }
    .money-input {
      min-height: 32px;
      padding: 0 12px;
      border-radius: 2px;
      background: $cinza-l90;
      border: 1px solid $cinza-l70;

      &:focus {
        outline: none;
      }
    }
  }

  .sku-input {
    width: 50px;
  }

  .empty-products-error {
    text-align: center;
    color: $vermelho-0;
  }

  .no-products-table {
    tr {
      th {
        color: #ff5252 !important;
      }
    }
  }

  .invalid-input {
    color: $vermelho-0;

    .field-padding {
      span {
        border: 1px #ff5252 solid !important;
        padding: 0 5px;
      }
    }
  }

  .invalid-qty {
    .v-input__control {
      border: 1px #ff5252 solid !important;
    }
  }

  .invalid-text-field {
    color: $vermelho-0;
    .theme--light.v-text-field--outlined.v-input--is-focused:not(.v-input--has-state):not(.v-input--is-disabled) > .v-input__control > .v-input__slot fieldset,
    .v-application .primary--text {
      border-color: white !important;
      caret-color: white !important;
      border: 0 !important;
      padding: 0 5px;
    }
    .field-padding {
      span {
        border: 1px #ff5252 solid !important;
      }
    }
    .v-input__control {
      border: 1px #ff5252 solid !important;
    }
  }
}

hr {
  border: 1px solid $cinza-l60;
}

h2 {
  font-size: 16px;
  font-weight: 600;
  color: $cinza-d10;
}
</style>
